import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Table,
  Tag,
  Button,
  Space,
  Tooltip,
  Modal,
  message,
  Spin,
  Empty,
  Statistic,
  Row,
  Col,
  Badge,
  Input,
  Select,
  Descriptions,
  Divider,
  Alert,
  Progress,
  InputNumber,
} from 'antd';
import {
  DollarOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SendOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  LinkOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  fetchOrganizerAccounts,
  fetchOrganizerAccount,
  refreshStripeStatus,
  fetchPendingPayouts,
  fetchEventPayoutSummary,
  transferToOrganizer,
  cancelPayout,
  fetchPayoutHistory,
  STRIPE_STATUS_COLORS,
  PAYOUT_STATUS_COLORS,
  formatCurrency,
  daysSinceEvent,
} from '../../server/payments/paymentsServer';

const { TabPane } = Tabs;
const { Option } = Select;
const { confirm } = Modal;

// Stripe Status Badge Component
const StripeStatusBadge = ({ status }) => {
  const color = STRIPE_STATUS_COLORS[status] || 'default';
  return <Tag color={color}>{status || 'Unknown'}</Tag>;
};

// Enabled/Disabled Badge
const EnabledBadge = ({ enabled, label }) => (
  <Tag color={enabled ? 'green' : 'red'} icon={enabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
    {label}: {enabled ? 'Yes' : 'No'}
  </Tag>
);

// Days Since Badge
const DaysSinceBadge = ({ days }) => {
  if (days === null) return <Tag>N/A</Tag>;
  let color = 'green';
  if (days > 30) color = 'red';
  else if (days > 7) color = 'orange';
  else if (days > 1) color = 'gold';
  return <Tag color={color}>{days} days ago</Tag>;
};

// Payout Status Badge
const PayoutStatusBadge = ({ status }) => {
  const color = PAYOUT_STATUS_COLORS[status] || 'default';
  const labels = {
    'pending': 'Pending',
    'partial': 'Partial',
    'completed': 'Completed',
    'Completed': 'Completed',
    'Pending': 'Pending',
    'Failed': 'Failed',
    'Reversed': 'Reversed',
    'cancelled': 'Cancelled',
  };
  return <Tag color={color}>{labels[status] || status || 'Unknown'}</Tag>;
};

const Orders = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(false);

  // Organizer Accounts State
  const [organizerAccounts, setOrganizerAccounts] = useState([]);
  const [selectedOrganizer, setSelectedOrganizer] = useState(null);
  const [organizerDetailVisible, setOrganizerDetailVisible] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(null);

  // Pending Payouts State
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutFilter, setPayoutFilter] = useState({ organizer: null, daysFilter: null, status: null });

  // Payout History State
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState({ status: null });
  const [historyLoading, setHistoryLoading] = useState(false);

  // Payout Modal State
  const [payoutModalVisible, setPayoutModalVisible] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [processingPayout, setProcessingPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState(null);
  const [payoutNotes, setPayoutNotes] = useState('');

  // Cancel Payout Modal State
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancellingPayout, setCancellingPayout] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Stats
  const [stats, setStats] = useState({
    totalOrganizers: 0,
    activeAccounts: 0,
    totalPendingPayouts: 0,
    totalPendingAmount: 0,
  });

  // Fetch organizer accounts
  const loadOrganizerAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetchOrganizerAccounts();
      if (response.success) {
        setOrganizerAccounts(response.data || []);
        // Calculate stats
        const accounts = response.data || [];
        setStats(prev => ({
          ...prev,
          totalOrganizers: accounts.length,
          activeAccounts: accounts.filter(a => a.stripe?.accountStatus === 'Active').length,
        }));
      }
    } catch (error) {
      console.error('Error loading organizer accounts:', error);
      message.error('Failed to load organizer accounts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch pending payouts
  const loadPendingPayouts = async () => {
    try {
      setLoading(true);
      const response = await fetchPendingPayouts();
      if (response.success) {
        setPendingPayouts(response.data || []);
        // Calculate stats - only count events with pending status
        const payouts = response.data || [];
        const pendingOnly = payouts.filter(p => p.payoutStatus !== 'completed');
        const totalAmount = pendingOnly.reduce((sum, p) => sum + (p.pendingPayout || 0), 0);
        setStats(prev => ({
          ...prev,
          totalPendingPayouts: pendingOnly.length,
          totalPendingAmount: totalAmount,
        }));
      }
    } catch (error) {
      console.error('Error loading pending payouts:', error);
      message.error('Failed to load pending payouts');
    } finally {
      setLoading(false);
    }
  };

  // Fetch payout history
  const loadPayoutHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await fetchPayoutHistory({ status: historyFilter.status, limit: 100 });
      if (response.success) {
        setPayoutHistory(response.data || []);
      }
    } catch (error) {
      console.error('Error loading payout history:', error);
      message.error('Failed to load payout history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handle refresh Stripe status
  const handleRefreshStripeStatus = async (accountId) => {
    try {
      setRefreshingStatus(accountId);
      await refreshStripeStatus(accountId);
      message.success('Stripe status refreshed');
      loadOrganizerAccounts();
    } catch (error) {
      console.error('Error refreshing status:', error);
      message.error('Failed to refresh Stripe status');
    } finally {
      setRefreshingStatus(null);
    }
  };

  // Handle view organizer detail
  const handleViewOrganizer = async (organizer) => {
    setSelectedOrganizer(organizer);
    setOrganizerDetailVisible(true);
  };

  // Handle release payout
  const handleReleasePayout = (payout) => {
    setSelectedPayout(payout);
    setPayoutAmount(payout.totalCollected);
    setPayoutNotes('');
    setPayoutModalVisible(true);
  };

  // Confirm and process payout
  const confirmPayout = async () => {
    if (!selectedPayout || !payoutAmount || payoutAmount <= 0) return;

    try {
      setProcessingPayout(true);
      const response = await transferToOrganizer({
        eventId: selectedPayout.eventId,
        organizerStripeAccountId: selectedPayout.organizerStripeAccountId,
        amount: payoutAmount,
        notes: payoutNotes || undefined,
      });

      if (response.success) {
        message.success(`Successfully transferred ${formatCurrency(response.data.amount)} to organizer`);
        setPayoutModalVisible(false);
        setSelectedPayout(null);
        setPayoutAmount(null);
        setPayoutNotes('');
        loadPendingPayouts();
      } else {
        message.error(response.message || 'Transfer failed');
      }
    } catch (error) {
      console.error('Error processing payout:', error);
      message.error(error.response?.data?.message || 'Failed to process payout');
    } finally {
      setProcessingPayout(false);
    }
  };

  // Handle cancel payout
  const handleCancelPayout = (payout) => {
    setSelectedPayout(payout);
    setCancelReason('');
    setCancelModalVisible(true);
  };

  // Confirm cancel payout
  const confirmCancelPayout = async () => {
    if (!selectedPayout || !cancelReason.trim()) {
      message.warning('Please provide a reason for cancellation');
      return;
    }

    try {
      setCancellingPayout(true);
      const response = await cancelPayout({
        eventId: selectedPayout.eventId,
        reason: cancelReason,
      });

      if (response.success) {
        message.success('Payout cancelled successfully');
        setCancelModalVisible(false);
        setSelectedPayout(null);
        setCancelReason('');
        loadPendingPayouts();
      } else {
        message.error(response.message || 'Failed to cancel payout');
      }
    } catch (error) {
      console.error('Error cancelling payout:', error);
      message.error(error.response?.data?.message || 'Failed to cancel payout');
    } finally {
      setCancellingPayout(false);
    }
  };

  // Load data on mount and tab change
  useEffect(() => {
    if (activeTab === 'organizers') {
      loadOrganizerAccounts();
    } else if (activeTab === 'pending') {
      loadPendingPayouts();
    } else if (activeTab === 'history') {
      loadPayoutHistory();
    }
  }, [activeTab]);

  // Reload history when filter changes
  useEffect(() => {
    if (activeTab === 'history') {
      loadPayoutHistory();
    }
  }, [historyFilter.status]);

  // Initial load
  useEffect(() => {
    loadPendingPayouts();
    loadOrganizerAccounts();
  }, []);

  // Filter pending payouts
  const filteredPayouts = pendingPayouts.filter(payout => {
    if (payoutFilter.organizer && payout.organizerId !== payoutFilter.organizer) return false;
    if (payoutFilter.daysFilter) {
      const days = daysSinceEvent(payout.eventEndTime);
      if (payoutFilter.daysFilter === '1' && days < 1) return false;
      if (payoutFilter.daysFilter === '7' && days < 7) return false;
      if (payoutFilter.daysFilter === '30' && days < 30) return false;
    }
    return true;
  });

  // Organizer Accounts Table Columns
  const organizerColumns = [
    {
      title: 'Organizer',
      key: 'organizer',
      render: (_, record) => (
        <div>
          <div className="font-semibold">{record.businessName || 'N/A'}</div>
          <div className="text-xs text-gray-500">@{record.profile?.username}</div>
        </div>
      ),
    },
    {
      title: 'Stripe Status',
      dataIndex: ['stripe', 'accountStatus'],
      key: 'status',
      render: (status) => <StripeStatusBadge status={status} />,
    },
    {
      title: 'Charges',
      dataIndex: ['stripe', 'chargesEnabled'],
      key: 'charges',
      render: (enabled) => <EnabledBadge enabled={enabled} label="Charges" />,
    },
    {
      title: 'Payouts',
      dataIndex: ['stripe', 'payoutsEnabled'],
      key: 'payouts',
      render: (enabled) => <EnabledBadge enabled={enabled} label="Payouts" />,
    },
    {
      title: 'Stripe Account',
      dataIndex: ['stripe', 'accountId'],
      key: 'accountId',
      render: (id) => id ? (
        <Tooltip title="Open in Stripe Dashboard">
          <a
            href={`https://dashboard.stripe.com/connect/accounts/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700"
          >
            {id.substring(0, 15)}... <LinkOutlined />
          </a>
        </Tooltip>
      ) : <Tag>Not Connected</Tag>,
    },
    {
      title: 'Revenue',
      dataIndex: ['financialSummary', 'combined', 'totalRevenue'],
      key: 'revenue',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Pending',
      dataIndex: ['financialSummary', 'combined', 'pendingBalance'],
      key: 'pending',
      render: (amount) => (
        <span className={amount > 0 ? 'text-orange-500 font-semibold' : ''}>
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewOrganizer(record)}
            />
          </Tooltip>
          <Tooltip title="Refresh Stripe Status">
            <Button
              icon={<ReloadOutlined spin={refreshingStatus === record._id} />}
              size="small"
              onClick={() => handleRefreshStripeStatus(record._id)}
              loading={refreshingStatus === record._id}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Pending Payouts Table Columns
  const payoutColumns = [
    {
      title: 'Organizer',
      key: 'organizer',
      render: (_, record) => (
        <div>
          <div className="font-semibold">{record.organizerName}</div>
          <div className="text-xs text-gray-400">{record.organizerStripeAccountId?.substring(0, 15)}...</div>
        </div>
      ),
    },
    {
      title: 'Event',
      key: 'event',
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.eventTitle}</div>
          <div className="text-xs text-gray-500">
            <CalendarOutlined className="mr-1" />
            {record.eventEndTime ? new Date(record.eventEndTime).toLocaleDateString() : 'N/A'}
          </div>
        </div>
      ),
    },
    {
      title: 'Days Since',
      key: 'daysSince',
      render: (_, record) => <DaysSinceBadge days={daysSinceEvent(record.eventEndTime)} />,
      sorter: (a, b) => daysSinceEvent(b.eventEndTime) - daysSinceEvent(a.eventEndTime),
    },
    {
      title: 'Tickets Sold',
      dataIndex: 'ticketsSold',
      key: 'tickets',
      render: (count) => <Tag color="blue">{count || 0} tickets</Tag>,
    },
    {
      title: 'Total Collected',
      dataIndex: 'totalCollected',
      key: 'collected',
      render: (amount) => (
        <span className="text-green-600 font-bold text-lg">{formatCurrency(amount)}</span>
      ),
      sorter: (a, b) => (a.totalCollected || 0) - (b.totalCollected || 0),
    },
    {
      title: 'Payout Status',
      dataIndex: 'payoutStatus',
      key: 'payoutStatus',
      render: (status) => <PayoutStatusBadge status={status || 'pending'} />,
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Partial', value: 'partial' },
        { text: 'Completed', value: 'completed' },
      ],
      onFilter: (value, record) => (record.payoutStatus || 'pending') === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const days = daysSinceEvent(record.eventEndTime);
        const isUpcoming = days !== null && days < 0;
        const isCompleted = record.payoutStatus === 'completed';

        if (isCompleted) {
          return (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Paid
            </Tag>
          );
        }

        return (
          <Space>
            <Tooltip title={isUpcoming ? 'Event not yet completed (release anyway?)' : 'Release Payout'}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                size="small"
                onClick={() => handleReleasePayout(record)}
                className={isUpcoming
                  ? "bg-orange-500 hover:bg-orange-600 border-orange-500"
                  : "bg-green-500 hover:bg-green-600 border-green-500"
                }
              >
                Release
              </Button>
            </Tooltip>
            <Tooltip title="Cancel Payout">
              <Button
                danger
                icon={<CloseCircleOutlined />}
                size="small"
                onClick={() => handleCancelPayout(record)}
              >
                Cancel
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
              <BankOutlined className="text-purple-500" />
              Payment Management
            </h1>
            <p className="text-gray-500 mt-1">Manage Stripe Connect payouts and organizer accounts</p>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadOrganizerAccounts();
              loadPendingPayouts();
            }}
            loading={loading}
          >
            Refresh All
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={16} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <Statistic
              title="Total Organizers"
              value={stats.totalOrganizers}
              prefix={<UserOutlined className="text-blue-500" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <Statistic
              title="Active Stripe Accounts"
              value={stats.activeAccounts}
              prefix={<CheckCircleOutlined className="text-green-500" />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <Statistic
              title="Pending Payouts"
              value={stats.totalPendingPayouts}
              prefix={<ClockCircleOutlined className="text-orange-500" />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card className="shadow-md hover:shadow-lg transition-shadow">
            <Statistic
              title="Total Pending Amount"
              value={stats.totalPendingAmount}
              prefix={<DollarOutlined className="text-green-500" />}
              precision={2}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Tabs */}
      <Card className="shadow-lg">
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
          {/* Pending Payouts Tab */}
          <TabPane
            tab={
              <span>
                <ClockCircleOutlined />
                Pending Payouts
                {stats.totalPendingPayouts > 0 && (
                  <Badge count={stats.totalPendingPayouts} className="ml-2" />
                )}
              </span>
            }
            key="pending"
          >
            {/* Filters */}
            <div className="mb-4 flex gap-4 flex-wrap">
              <Select
                placeholder="Filter by Organizer"
                allowClear
                style={{ width: 200 }}
                onChange={(val) => setPayoutFilter(prev => ({ ...prev, organizer: val }))}
              >
                {organizerAccounts.map(org => (
                  <Option key={org._id} value={org._id}>{org.businessName}</Option>
                ))}
              </Select>
              <Select
                placeholder="Days Since Event"
                allowClear
                style={{ width: 180 }}
                onChange={(val) => setPayoutFilter(prev => ({ ...prev, daysFilter: val }))}
              >
                <Option value="1">More than 1 day</Option>
                <Option value="7">More than 7 days</Option>
                <Option value="30">More than 30 days</Option>
              </Select>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <Spin size="large" />
              </div>
            ) : filteredPayouts.length === 0 ? (
              <Empty description="No pending payouts" />
            ) : (
              <Table
                columns={payoutColumns}
                dataSource={filteredPayouts}
                rowKey="eventId"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
              />
            )}
          </TabPane>

          {/* Organizer Accounts Tab */}
          <TabPane
            tab={
              <span>
                <BankOutlined />
                Organizer Accounts
              </span>
            }
            key="organizers"
          >
            {loading ? (
              <div className="flex justify-center py-12">
                <Spin size="large" />
              </div>
            ) : organizerAccounts.length === 0 ? (
              <Empty description="No organizer accounts found" />
            ) : (
              <Table
                columns={organizerColumns}
                dataSource={organizerAccounts}
                rowKey="_id"
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1200 }}
              />
            )}
          </TabPane>

          {/* Payout History Tab */}
          <TabPane
            tab={
              <span>
                <CheckCircleOutlined />
                Payout History
              </span>
            }
            key="history"
          >
            {/* History Filters */}
            <div className="mb-4 flex gap-4 flex-wrap items-center">
              <Select
                placeholder="Filter by Status"
                allowClear
                style={{ width: 180 }}
                value={historyFilter.status}
                onChange={(val) => setHistoryFilter(prev => ({ ...prev, status: val }))}
              >
                <Option value="Completed">Completed</Option>
                <Option value="Pending">Pending</Option>
                <Option value="Failed">Failed</Option>
                <Option value="Reversed">Reversed</Option>
              </Select>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadPayoutHistory}
                loading={historyLoading}
              >
                Refresh
              </Button>
              <Button
                type="link"
                href="https://dashboard.stripe.com/connect/transfers"
                target="_blank"
                icon={<LinkOutlined />}
              >
                View in Stripe
              </Button>
            </div>

            {historyLoading ? (
              <div className="flex justify-center py-12">
                <Spin size="large" />
              </div>
            ) : payoutHistory.length === 0 ? (
              <Empty description="No payout history found" />
            ) : (
              <Table
                columns={[
                  {
                    title: 'Date',
                    dataIndex: 'createdAt',
                    key: 'date',
                    width: 150,
                    render: (date) => date ? new Date(date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-',
                    sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
                    defaultSortOrder: 'descend',
                  },
                  {
                    title: 'Event',
                    key: 'event',
                    render: (_, record) => (
                      <div>
                        <div className="font-medium">{record.eventId?.title || record.eventTitle || 'Unknown Event'}</div>
                        <div className="text-xs text-gray-500">{record.eventId?._id || record.eventId}</div>
                      </div>
                    ),
                  },
                  {
                    title: 'Organizer',
                    key: 'organizer',
                    render: (_, record) => (
                      <div>
                        <div className="font-semibold">{record.organizerId?.businessName || record.organizerName || 'N/A'}</div>
                        <div className="text-xs text-gray-400">{record.stripeAccountId?.substring(0, 15)}...</div>
                      </div>
                    ),
                  },
                  {
                    title: 'Amount',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (amount, record) => (
                      <span className="text-green-600 font-bold">
                        {formatCurrency(amount, record.currency?.toUpperCase())}
                      </span>
                    ),
                    sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
                  },
                  {
                    title: 'Transfer ID',
                    dataIndex: 'stripeTransferId',
                    key: 'transferId',
                    render: (id) => id ? (
                      <Tooltip title="Open in Stripe Dashboard">
                        <a
                          href={`https://dashboard.stripe.com/connect/transfers/${id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-700 text-xs"
                        >
                          {id.substring(0, 20)}... <LinkOutlined />
                        </a>
                      </Tooltip>
                    ) : '-',
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status) => <PayoutStatusBadge status={status} />,
                  },
                ]}
                dataSource={payoutHistory}
                rowKey="_id"
                pagination={{ pageSize: 15, showSizeChanger: true }}
                scroll={{ x: 1000 }}
              />
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Organizer Detail Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <BankOutlined className="text-purple-500" />
            Organizer Details
          </div>
        }
        open={organizerDetailVisible}
        onCancel={() => setOrganizerDetailVisible(false)}
        footer={null}
        width={700}
      >
        {selectedOrganizer && (
          <div className="space-y-4">
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Business Name" span={2}>
                <strong>{selectedOrganizer.businessName}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Profile">
                @{selectedOrganizer.profile?.username}
              </Descriptions.Item>
              <Descriptions.Item label="Stripe Account">
                {selectedOrganizer.stripe?.accountId ? (
                  <a
                    href={`https://dashboard.stripe.com/connect/accounts/${selectedOrganizer.stripe.accountId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selectedOrganizer.stripe.accountId} <LinkOutlined />
                  </a>
                ) : 'Not Connected'}
              </Descriptions.Item>
              <Descriptions.Item label="Account Status">
                <StripeStatusBadge status={selectedOrganizer.stripe?.accountStatus} />
              </Descriptions.Item>
              <Descriptions.Item label="Capabilities">
                {selectedOrganizer.stripe?.capabilities?.join(', ') || 'None'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Financial Summary</Divider>

            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Total Revenue"
                  value={selectedOrganizer.financialSummary?.combined?.totalRevenue || 0}
                  formatter={(val) => formatCurrency(val)}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Total Paid Out"
                  value={selectedOrganizer.financialSummary?.combined?.totalPaidOut || 0}
                  formatter={(val) => formatCurrency(val)}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Pending Balance"
                  value={selectedOrganizer.financialSummary?.combined?.pendingBalance || 0}
                  formatter={(val) => formatCurrency(val)}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
            </Row>

            <Divider>Platform Fees</Divider>

            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Stripe Fee %">
                {(selectedOrganizer.platformFees?.stripe?.percentage * 100) || 10}%
              </Descriptions.Item>
              <Descriptions.Item label="Fixed Amount">
                {formatCurrency(selectedOrganizer.platformFees?.stripe?.fixedAmount || 0.30)}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Release Payout Confirmation Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <SendOutlined className="text-green-500" />
            Confirm Payout Release
          </div>
        }
        open={payoutModalVisible}
        onCancel={() => {
          setPayoutModalVisible(false);
          setSelectedPayout(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => setPayoutModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            icon={<SendOutlined />}
            onClick={confirmPayout}
            loading={processingPayout}
            className="bg-green-500 hover:bg-green-600 border-green-500"
          >
            Confirm Transfer
          </Button>,
        ]}
        width={500}
      >
        {selectedPayout && (
          <div className="space-y-4">
            <Alert
              message="Review Before Transferring"
              description="Make sure the event has completed and there are no pending disputes. The transfer will be processed in CAD via Stripe."
              type="warning"
              showIcon
              className="mb-4"
            />

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Event">
                <strong>{selectedPayout.eventTitle}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Organizer">
                {selectedPayout.organizerName}
              </Descriptions.Item>
              <Descriptions.Item label="Event Date">
                {selectedPayout.eventEndTime ? new Date(selectedPayout.eventEndTime).toLocaleDateString() : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Tickets Sold">
                {selectedPayout.ticketsSold || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Total Collected">
                {formatCurrency(selectedPayout.totalCollected)}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <div className="text-gray-500 text-sm mb-2 text-center">Amount to Transfer (CAD)</div>
              <div className="flex justify-center">
                <InputNumber
                  value={payoutAmount}
                  onChange={(value) => setPayoutAmount(value)}
                  min={0.01}
                  max={selectedPayout.totalCollected}
                  step={0.01}
                  precision={2}
                  prefix="$"
                  size="large"
                  style={{ width: 200, fontSize: '20px' }}
                />
              </div>
              {payoutAmount !== null && payoutAmount < selectedPayout.totalCollected && (
                <Alert
                  message="Partial Payout"
                  description={`Remaining ${formatCurrency(selectedPayout.totalCollected - payoutAmount)} will stay pending.`}
                  type="info"
                  showIcon
                  className="mt-3"
                />
              )}
            </div>

            <div>
              <div className="text-gray-500 text-sm mb-1">Notes (optional)</div>
              <Input.TextArea
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
                placeholder="Add notes about this transfer..."
                rows={2}
              />
            </div>

            <div className="text-xs text-gray-400 mt-4">
              Transfer to: {selectedPayout.organizerStripeAccountId}
            </div>
          </div>
        )}
      </Modal>

      {/* Cancel Payout Confirmation Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <CloseCircleOutlined className="text-red-500" />
            Cancel Payout
          </div>
        }
        open={cancelModalVisible}
        onCancel={() => {
          setCancelModalVisible(false);
          setSelectedPayout(null);
          setCancelReason('');
        }}
        footer={[
          <Button key="back" onClick={() => setCancelModalVisible(false)}>
            Go Back
          </Button>,
          <Button
            key="confirm"
            danger
            type="primary"
            icon={<CloseCircleOutlined />}
            onClick={confirmCancelPayout}
            loading={cancellingPayout}
            disabled={!cancelReason.trim()}
          >
            Confirm Cancel
          </Button>,
        ]}
        width={500}
      >
        {selectedPayout && (
          <div className="space-y-4">
            <Alert
              message="This will cancel the pending payout"
              description="The organizer will not receive funds for this event. This action can be undone by creating a new payout later."
              type="error"
              showIcon
              className="mb-4"
            />

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Event">
                <strong>{selectedPayout.eventTitle}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Organizer">
                {selectedPayout.organizerName}
              </Descriptions.Item>
              <Descriptions.Item label="Pending Amount">
                <span className="text-red-500 font-bold">
                  {formatCurrency(selectedPayout.totalCollected)}
                </span>
              </Descriptions.Item>
            </Descriptions>

            <div>
              <div className="text-gray-500 text-sm mb-1">Reason for cancellation <span className="text-red-500">*</span></div>
              <Input.TextArea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Provide a reason for cancelling this payout..."
                rows={3}
                status={!cancelReason.trim() ? 'error' : undefined}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Orders;
